import { useCallback, useMemo, useRef, useState } from "react";
import { Box, Button, TextField, Theme, Typography, alpha } from "@mui/material";
import { GridCellParams, GridColDef, GridValueGetterParams } from "@mui/x-data-grid";
import moment from "moment";
import {
  IVacationAwardingCrew,
  IVacationAwardingResponse,
  IVacationChangeRequest,
  VacationBidRound,
} from "@vmosolutions/oc-interfaces";

import {
  EditableDataGrid,
  dataCol,
  readOnlyTextColumn,
  useEditableGridPagination,
} from "src/components/EditableDataGrid";

import { VACATION_BID_TEST_ID } from "../testIds";
import { displaySlotId } from "../helpers";
import {
  awardDateRange,
  awardSourceLabel,
  awardedDays,
  crewIsShort,
  crewMatchesSearch,
  crewName,
  entitlementUse,
  leftoverPlacement,
  openWarnings,
  shortBlockLabel,
  sideLabel,
} from "./awardingHelpers";
import { AwardWarnings } from "./AwardWarnings";
import { RankedBidList } from "./RankedBidList";
import { ChangeRequestHistory } from "../ChangeRequests/ChangeRequestHistory";
import { awardedBeforeChanges, requestHistoryOf } from "../ChangeRequests/changeRequestHelpers";

export interface IViewBidsTabProps {
  view: IVacationAwardingResponse | null;
  loading: boolean;
  /** The round whose bids are shown, and every round the package runs to choose from. */
  round: VacationBidRound;
  rounds: VacationBidRound[];
  onRoundChange: (round: VacationBidRound) => void;
  showSlotIdYear: boolean;
  onRefresh: () => void;
  /** Every change request in the package, shown per crew member as their change history. */
  changeRequests?: IVacationChangeRequest[];
  /** The package base's time zone, for the history's times. */
  timezoneId?: string;
  /** Resolves true once the crew member's warning is gone. */
  onDismissWarning: (userId: string, warningId: string) => Promise<boolean>;
}

interface IBidRow {
  userId: string;
  seniority: string;
  name: string;
  submitted: boolean;
  submittedAt: string;
  ranked: number;
  /** Blocks awarded against the entitlement, or days when it has no block count. */
  awarded: string;
  /** Whole blocks of the entitlement not yet awarded. Null when it has no block count. */
  unfilled: number | null;
  /** Entitlement change warnings still open on them. */
  warnings: number;
  note: string;
}

const getBidRowId = (row: IBidRow): string => row.userId;

const noEdits = (): void => undefined;

const ViewBidsTabStyles = (theme: Theme): any => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  height: "100%",
  boxSizing: "border-box",
  padding: "10px",
  position: "relative",
  "& .view_bids_toolbar": {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    marginBottom: "5px",
    "& .view_bids_caption": { flex: 1, fontSize: 12, color: theme.custom.forms.labels },
    "& .MuiButtonBase-root": { padding: "2px 8px", fontSize: 12 },
    "& .view_bids_search": {
      width: "220px",
      "& .MuiInputBase-root": { backgroundColor: theme.custom.forms.inputBack, color: theme.custom.forms.inputText },
      "& .MuiOutlinedInput-notchedOutline": { borderColor: theme.custom.forms.inputBorder },
      "& .MuiInputBase-input": { fontSize: 13, padding: "4px 8px" },
    },
  },
  "& .view_bids_panes": {
    flex: 1,
    minHeight: "300px",
    display: "flex",
    gap: "10px",
    minWidth: 0,
  },
  "& .view_bids_grid": {
    flex: 3,
    minWidth: 0,
    position: "relative",
    backgroundColor: theme.palette.background.paper,
    // Pinned out of flow so the grid's size comes only from this container.
    "& > .MuiDataGrid-root": { position: "absolute", top: 0, right: 0, bottom: 0, left: 0, border: "none" },
    "& .MuiDataGrid-row": { cursor: "pointer" },
    "& .MuiDataGrid-row.selected_row": { backgroundColor: alpha(theme.palette.primary.main, 0.2) },
    "& .MuiDataGrid-cell.short_cell": { color: theme.palette.warning.main, fontWeight: 700 },
    "& .MuiDataGrid-cell.warning_cell": { color: theme.palette.warning.light, fontWeight: 700 },
  },
  "& .view_bids_detail": {
    flex: 2,
    minWidth: 0,
    overflowY: "auto",
    // The award and bid rows keep to one line; a narrow pane scrolls them sideways instead.
    overflowX: "auto",
    padding: "10px",
    backgroundColor: theme.palette.background.paper,
    "& .detail_heading": { fontWeight: 700, fontSize: 15 },
    "& .detail_meta": { color: theme.custom.forms.labels, marginBottom: "8px" },
    "& .detail_label": {
      fontSize: 11,
      textTransform: "uppercase",
      color: theme.custom.forms.labels,
      margin: "10px 0 4px",
      "& .detail_label_hint": { textTransform: "none", marginLeft: "6px" },
    },
    // Same colours the Award Bids panel uses for the same two states, so the tabs agree.
    "& .detail_leftover_placed": {
      fontSize: 13,
      color: theme.palette.success.main,
      margin: theme.spacing(0.25, 0, 0.5, 0),
    },
    "& .detail_leftover_unplaced": {
      fontSize: 13,
      color: theme.palette.warning.light,
      margin: theme.spacing(0.25, 0, 0.5, 0),
    },
    "& .detail_leftover": {
      display: "grid",
      gridTemplateColumns: "28px 60px minmax(0, 1fr)",
      columnGap: "12px",
      padding: "1px 10px",
      "& .rank": { color: theme.custom.forms.labels },
      "& .slot_id": { fontWeight: 700, whiteSpace: "nowrap" },
    },
    "& .detail_note": {
      padding: "6px 10px",
      borderLeft: `3px solid ${theme.palette.primary.main}`,
      backgroundColor: theme.custom.timeline.contextListBackground,
    },
    "& .detail_empty": { color: theme.custom.forms.labels, textAlign: "center", marginTop: "40px" },
    "& .detail_awards": { display: "flex", flexDirection: "column", gap: "4px", minWidth: "max-content" },
    "& .detail_award": {
      display: "flex",
      gap: "10px",
      alignItems: "baseline",
      padding: "4px 8px",
      borderRadius: "3px",
      backgroundColor: theme.custom.timeline.contextListBackground,
      "& > *": { whiteSpace: "nowrap" },
      "& .award_id": { fontWeight: 700, minWidth: "90px" },
      "& .award_days": { fontSize: 11, color: theme.custom.forms.labels },
      "& .award_source": { marginLeft: "auto", fontSize: 11, color: theme.custom.forms.labels },
    },
    "& .detail_short": { color: theme.palette.warning.main },
  },
});

/** Awards against the entitlement, counted in blocks, or in days when it has no block count. */
const awardedText = (crew: IVacationAwardingCrew): string => {
  const { blocks, awarded, totalDays } = entitlementUse(crew);
  return blocks === null
    ? `${awardedDays(crew)} of ${totalDays} days`
    : `${awarded} of ${blocks} block${blocks === 1 ? "" : "s"}`;
};

const toRow = (crew: IVacationAwardingCrew): IBidRow => ({
  userId: crew.userId,
  seniority: crew.seniority === null ? "-" : String(crew.seniority),
  name: crewName(crew),
  submitted: !!crew.bid,
  submittedAt: crew.bid?.submittedAt ? moment(crew.bid.submittedAt).format("YYYY-MM-DD HH:mm") : "",
  ranked: crew.bid?.preferences.length ?? 0,
  awarded: awardedText(crew),
  unfilled: entitlementUse(crew).remaining,
  warnings: openWarnings(crew).length,
  note: crew.bid?.note ?? "",
});

/**
 * View Bids tab - every crew member, most senior first, with their bid, awards and unfilled entitlement.
 * Read-only, and usable while the bid window is still open.
 */
export const ViewBidsTab = (props: IViewBidsTabProps): JSX.Element => {
  const {
    view,
    loading,
    round,
    rounds,
    onRoundChange,
    showSlotIdYear,
    onRefresh,
    changeRequests,
    timezoneId,
    onDismissWarning,
  } = props;

  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [shortOnly, setShortOnly] = useState<boolean>(false);
  const [notSubmittedOnly, setNotSubmittedOnly] = useState<boolean>(false);
  const [search, setSearch] = useState<string>("");
  const gridContainerRef = useRef<HTMLDivElement | null>(null);

  const crew = useMemo(() => view?.crew ?? [], [view]);
  const slotsById = useMemo(() => new Map((view?.slots ?? []).map((z) => [z.slotId, z])), [view]);
  const shown = useMemo(
    () =>
      crew.filter(
        (member) =>
          (!shortOnly || crewIsShort(member)) && (!notSubmittedOnly || !member.bid) && crewMatchesSearch(member, search)
      ),
    [crew, shortOnly, notSubmittedOnly, search]
  );
  const rows = useMemo(() => shown.map(toRow), [shown]);
  const selected = useMemo(() => shown.find((z) => z.userId === selectedUserId) ?? null, [shown, selectedUserId]);
  const submitted = useMemo(() => crew.filter((z) => z.bid).length, [crew]);
  const short = useMemo(() => crew.filter(crewIsShort).length, [crew]);
  const selectedUnfilled = selected ? entitlementUse(selected).remaining : null;
  const selectedHistory = useMemo(
    () => (selected ? requestHistoryOf(changeRequests ?? [], selected.userId) : []),
    [changeRequests, selected]
  );

  // What they asked for at bid time, best first. Only crew with an uneven entitlement have any.
  const leftover = useMemo(() => {
    const asked = selected?.bid?.leftoverDays;
    return asked?.placements.length
      ? { ...asked, placements: [...asked.placements].sort((a, b) => a.rank - b.rank) }
      : null;
  }, [selected]);

  // Where the days actually ended up, next to the awards this tab already marks.
  const placedLeftover = useMemo(() => (selected ? leftoverPlacement(selected) : null), [selected]);
  const usageBySlot = useMemo(() => new Map((view?.usage ?? []).map((z) => [z.slotId, z])), [view]);

  const { paginationModel, onPaginationModelChange } = useEditableGridPagination({
    rows,
    getRowId: getBidRowId,
    containerRef: gridContainerRef,
  });

  const handleRowClick = useCallback((row: IBidRow) => setSelectedUserId(row.userId), []);
  const rowClassName = useCallback(
    (row: IBidRow): string => (row.userId === selectedUserId ? "selected_row" : ""),
    [selectedUserId]
  );

  const columns = useMemo<GridColDef<IBidRow>[]>(
    () => [
      readOnlyTextColumn<IBidRow>("seniority", "Seniority", { flex: 5 }),
      dataCol<IBidRow>({ field: "name", headerName: "Name", flex: 10, align: "left", headerAlign: "left" }),
      readOnlyTextColumn<IBidRow>("submittedAt", "Submitted", { flex: 7 }),
      readOnlyTextColumn<IBidRow>("ranked", "Ranked", {
        flex: 4,
        // Blank rather than 0 for crew who have not submitted, matching the empty Submitted cell.
        valueGetter: (params: GridValueGetterParams<IBidRow>) => (params.row.submitted ? params.row.ranked : ""),
      }),
      readOnlyTextColumn<IBidRow>("awarded", "Awarded", { flex: 6 }),
      dataCol<IBidRow>({
        field: "unfilled",
        headerName: "Unfilled",
        flex: 4,
        editable: false,
        // Blank rather than 0 for crew who are not short, so the short ones stand out.
        valueGetter: (params: GridValueGetterParams<IBidRow>) => (params.row.unfilled ? params.row.unfilled : ""),
        cellClassName: (params: GridCellParams<IBidRow>) => (params.row.unfilled ? "short_cell" : ""),
      }),
      dataCol<IBidRow>({
        field: "warnings",
        headerName: "Warnings",
        flex: 4,
        editable: false,
        valueGetter: (params: GridValueGetterParams<IBidRow>) => (params.row.warnings ? params.row.warnings : ""),
        cellClassName: (params: GridCellParams<IBidRow>) => (params.row.warnings ? "warning_cell" : ""),
      }),
      dataCol<IBidRow>({ field: "note", headerName: "Note", flex: 12, align: "left", headerAlign: "left" }),
    ],
    []
  );

  return (
    <Box className="view_bids_tab" sx={ViewBidsTabStyles}>
      <div className="view_bids_toolbar">
        {rounds.length > 1 &&
          rounds.map((option) => (
            <Button
              key={option}
              size="small"
              variant={option === round ? "contained" : "outlined"}
              color={option === round ? "secondary" : "primary"}
              disabled={loading}
              onClick={() => onRoundChange(option)}
              data-testid={VACATION_BID_TEST_ID.viewBidsRoundButton}
            >
              {`Round ${option}`}
            </Button>
          ))}
        <Typography className="view_bids_caption" variant="body2">
          {view
            ? `Round ${view.round} · ${crew.length} crew · ${submitted} submitted · ` +
              `${crew.length - submitted} not submitted · ${short} short of their entitlement`
            : ""}
        </Typography>
        <TextField
          className="view_bids_search"
          size="small"
          placeholder="Search name or employee id"
          value={search}
          disabled={!view}
          onChange={(event) => setSearch(event.target.value)}
          // This tab sits inside the editor's form, where Enter would submit unsaved package edits.
          onKeyDown={(event) => {
            if (event.key === "Enter") event.preventDefault();
          }}
          inputProps={{ "data-testid": VACATION_BID_TEST_ID.viewBidsSearch }}
        />
        <Button
          size="small"
          variant={notSubmittedOnly ? "contained" : "outlined"}
          aria-pressed={notSubmittedOnly}
          disabled={!view}
          onClick={() => setNotSubmittedOnly((current) => !current)}
          data-testid={VACATION_BID_TEST_ID.notSubmittedOnlyToggle}
        >
          Not submitted only
        </Button>
        <Button
          size="small"
          variant={shortOnly ? "contained" : "outlined"}
          aria-pressed={shortOnly}
          disabled={!view}
          onClick={() => setShortOnly((current) => !current)}
          data-testid={VACATION_BID_TEST_ID.shortOnlyToggle}
        >
          Short only
        </Button>
        <Button
          size="small"
          variant="outlined"
          disabled={loading}
          onClick={onRefresh}
          data-testid={VACATION_BID_TEST_ID.refreshBidsButton}
        >
          Refresh
        </Button>
      </div>

      <div className="view_bids_panes">
        <Box ref={gridContainerRef} className="view_bids_grid">
          <EditableDataGrid<IBidRow>
            data-testid={VACATION_BID_TEST_ID.viewBidsGrid}
            rowTestId={VACATION_BID_TEST_ID.viewBidsRow}
            rows={rows}
            columns={columns}
            getRowId={getBidRowId}
            onRowsChange={noEdits}
            paginationModel={paginationModel}
            onPaginationModelChange={onPaginationModelChange}
            disableRowSelectionOnClick={true}
            onRowClick={handleRowClick}
            getRowClassName={rowClassName}
            loading={loading}
          />
        </Box>

        <div className="view_bids_detail">
          {!selected && (
            <Typography variant="body2" className="detail_empty">
              Select a crew member to see what they bid and hold.
            </Typography>
          )}
          {selected && (
            <>
              <Typography className="detail_heading">{crewName(selected)}</Typography>
              <Typography variant="body2" className="detail_meta">
                {selected.seniority === null ? "No seniority" : `Seniority ${selected.seniority}`}
                {` · ${selected.entitlement.totalDays} days total`}
              </Typography>
              <Typography variant="body2" className={selectedUnfilled ? "detail_short" : "detail_meta"}>
                {awardedText(selected)}
                {selectedUnfilled ? ` · ${selectedUnfilled} unfilled` : ""}
              </Typography>
              <AwardWarnings
                warnings={openWarnings(selected)}
                onDismiss={(warningId) => onDismissWarning(selected.userId, warningId)}
              />
              <Typography className="detail_label">{`Awards (${selected.awards.length})`}</Typography>
              {selected.awards.length === 0 ? (
                <Typography variant="body2" className="detail_meta">
                  Nothing awarded yet.
                </Typography>
              ) : (
                <div className="detail_awards">
                  {selected.awards.map((award) => {
                    const slot = slotsById.get(award.slotId);
                    return (
                      <div key={award.slotId} className="detail_award">
                        <Typography variant="body2" className="award_id">
                          {displaySlotId(award.slotId, showSlotIdYear)}
                        </Typography>
                        <Typography variant="body2">
                          {slot ? awardDateRange(award, slot) : "No longer offered"}
                        </Typography>
                        {slot && shortBlockLabel(award, slot) && (
                          <Typography variant="caption" className="award_days">
                            {shortBlockLabel(award, slot)}
                          </Typography>
                        )}
                        <Typography variant="caption" className="award_source">
                          {awardSourceLabel(award)}
                        </Typography>
                      </div>
                    );
                  })}
                </div>
              )}
              {(leftover || placedLeftover) && (
                <>
                  <Typography className="detail_label">
                    Leftover days
                    {leftover && (
                      <span className="detail_label_hint">
                        {`${leftover.days} day${leftover.days === 1 ? "" : "s"} on one of these, best first`}
                      </span>
                    )}
                  </Typography>
                  <Typography
                    variant="body2"
                    className={placedLeftover ? "detail_leftover_placed" : "detail_leftover_unplaced"}
                  >
                    {placedLeftover
                      ? `${placedLeftover.days} day${placedLeftover.days === 1 ? "" : "s"} ` +
                        `${sideLabel(placedLeftover.side).toLowerCase()} ` +
                        `${displaySlotId(placedLeftover.slotId, showSlotIdYear)}`
                      : "Not placed yet."}
                  </Typography>
                  {leftover?.placements.map((placement) => (
                    <Typography
                      key={`${placement.slotId} ${placement.side}`}
                      variant="body2"
                      className="detail_leftover"
                      data-testid={VACATION_BID_TEST_ID.leftoverChoiceRow}
                    >
                      <span className="rank">{placement.rank}</span>
                      <span>{sideLabel(placement.side)}</span>
                      <span className="slot_id">{displaySlotId(placement.slotId, showSlotIdYear)}</span>
                    </Typography>
                  ))}
                </>
              )}
              {!selected.bid && (
                <Typography variant="body2" className="detail_empty">
                  No bid submitted.
                </Typography>
              )}
              {selected.bid?.note && (
                <>
                  <Typography className="detail_label">Package note</Typography>
                  <Typography variant="body2" className="detail_note">
                    {selected.bid.note}
                  </Typography>
                </>
              )}
              {selected.bid && (
                <>
                  <Typography className="detail_label">{`Ranked bids (${selected.bid.preferences.length})`}</Typography>
                  <RankedBidList
                    preferences={selected.bid.preferences}
                    slotsById={slotsById}
                    usageBySlot={usageBySlot}
                    showSlotIdYear={showSlotIdYear}
                    awardedSlotIds={awardedBeforeChanges(selected.awards, selectedHistory, round)}
                    compact={true}
                  />
                </>
              )}
              {selectedHistory.length > 0 && (
                <>
                  <Typography className="detail_label">{`Change requests (${selectedHistory.length})`}</Typography>
                  <ChangeRequestHistory
                    requests={selectedHistory}
                    slotsById={slotsById}
                    showSlotIdYear={showSlotIdYear}
                    timezoneId={timezoneId}
                  />
                </>
              )}
            </>
          )}
        </div>
      </div>
    </Box>
  );
};

export default ViewBidsTab;
